// src/pages/OrderSuccess.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  CheckCircle, Package, Home, MapPin, Phone, Mail,
  ShoppingBag, Truck, User, Hash, Calendar, CreditCard
} from 'lucide-react';

const OrderSuccess = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const docRef = doc(db, 'orders', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setOrder({ id: docSnap.id, ...docSnap.data() });
        }
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
    window.scrollTo(0, 0);
  }, [id]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-PK', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-body-bg)' }}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
            style={{ borderColor: 'var(--color-link)', borderTopColor: 'transparent' }}></div>
          <p className="mt-4" style={{ color: 'var(--color-secondary-text)' }}>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--color-body-bg)' }}>
        <div className="text-center anim-fadeInUp max-w-md">
          <Package size={64} className="mx-auto mb-4 anim-float" style={{ color: 'var(--color-secondary-text)' }} />
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-primary-text)' }}>Order Not Found</h2>
          <p className="mb-6" style={{ color: 'var(--color-secondary-text)' }}>
            Ye order ID exist nahi karta ya remove ho gaya hai.
          </p>
          <Link to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold btn-2d shadow-lg"
            style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}>
            <Home size={18} /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const orderShortId = order.id.slice(-8).toUpperCase();

  return (
    <div className="min-h-screen py-6 anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)' }}>
      <div className="max-w-4xl mx-auto px-4 md:px-6">

        {/* ================= HERO / THANK YOU ================= */}
        <div
          className="rounded-3xl p-8 md:p-12 text-center mb-6 anim-scaleIn relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, var(--color-button-bg) 0%, var(--color-button-hover-bg) 100%)`,
            color: '#FFFFFF',
          }}
        >
          {/* Confetti-like decoration */}
          <div className="absolute top-4 left-6 w-3 h-3 rounded-full opacity-60 anim-float"
            style={{ backgroundColor: '#FFFFFF' }}></div>
          <div className="absolute top-12 right-8 w-2 h-2 rounded-full opacity-50 anim-float"
            style={{ backgroundColor: '#FFFFFF', animationDelay: '0.5s' }}></div>
          <div className="absolute bottom-6 left-12 w-2.5 h-2.5 rounded-full opacity-60 anim-float"
            style={{ backgroundColor: '#FFFFFF', animationDelay: '1s' }}></div>
          <div className="absolute bottom-10 right-16 w-3 h-3 rounded-full opacity-40 anim-float"
            style={{ backgroundColor: '#FFFFFF', animationDelay: '1.5s' }}></div>

          <div className="flex justify-center mb-5">
            <div className="w-24 h-24 rounded-full flex items-center justify-center anim-popIn"
              style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
              <CheckCircle size={56} strokeWidth={2.5} />
            </div>
          </div>

          <h1 className="text-3xl md:text-5xl font-bold mb-3 anim-fadeInUp">
            Thank You!
          </h1>
          <p className="text-base md:text-lg opacity-95 mb-2 anim-fadeInUp delay-100">
            Aapka order successfully place ho gaya hai
          </p>
          <p className="text-sm md:text-base opacity-80 anim-fadeInUp delay-200">
            Hum jald hi aap se contact karenge
          </p>

          {/* Order ID Badge */}
          <div
            className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-full backdrop-blur-md anim-fadeInUp delay-300"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
          >
            <Hash size={18} />
            <span className="text-sm font-semibold">Order ID:</span>
            <span className="text-lg font-bold tracking-wider">{orderShortId}</span>
          </div>
        </div>

        {/* ================= ORDER DETAILS CARD ================= */}
        <div
          className="rounded-2xl p-6 mb-6 anim-fadeInUp"
          style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}
        >
          <h2 className="text-xl font-bold mb-5 flex items-center gap-2"
            style={{ color: 'var(--color-primary-text)' }}>
            <ShoppingBag size={22} style={{ color: 'var(--color-link)' }} />
            Order Details
          </h2>

          {/* Meta Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div className="flex items-start gap-3 p-3 rounded-xl" style={{ backgroundColor: '#FFF2F6' }}>
              <Calendar size={18} className="mt-0.5 shrink-0" style={{ color: 'var(--color-link)' }} />
              <div>
                <p className="text-xs" style={{ color: 'var(--color-secondary-text)' }}>Order Date</p>
                <p className="text-sm font-semibold" style={{ color: 'var(--color-primary-text)' }}>
                  {formatDate(order.createdAt)}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-xl" style={{ backgroundColor: '#FFF2F6' }}>
              <Truck size={18} className="mt-0.5 shrink-0" style={{ color: 'var(--color-link)' }} />
              <div>
                <p className="text-xs" style={{ color: 'var(--color-secondary-text)' }}>Status</p>
                <p className="text-sm font-semibold capitalize" style={{ color: 'var(--color-primary-text)' }}>
                  {order.status || 'Processing'}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-xl" style={{ backgroundColor: '#FFF2F6' }}>
              <CreditCard size={18} className="mt-0.5 shrink-0" style={{ color: 'var(--color-link)' }} />
              <div>
                <p className="text-xs" style={{ color: 'var(--color-secondary-text)' }}>Payment Method</p>
                <p className="text-sm font-semibold capitalize" style={{ color: 'var(--color-primary-text)' }}>
                  {order.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Bank Transfer'}
                </p>
              </div>
            </div>
          </div>

          {/* Customer + Shipping Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl p-4" style={{ backgroundColor: '#FFF2F6' }}>
              <h3 className="font-bold text-sm mb-3 flex items-center gap-2"
                style={{ color: 'var(--color-primary-text)' }}>
                <User size={16} style={{ color: 'var(--color-link)' }} /> Customer Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <User size={14} style={{ color: 'var(--color-secondary-text)' }} />
                  <span style={{ color: 'var(--color-primary-text)' }}>{order.customerName || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail size={14} style={{ color: 'var(--color-secondary-text)' }} />
                  <span style={{ color: 'var(--color-primary-text)' }}>{order.customerEmail || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={14} style={{ color: 'var(--color-secondary-text)' }} />
                  <span style={{ color: 'var(--color-primary-text)' }}>{order.phone || '-'}</span>
                </div>
              </div>
            </div>

            <div className="rounded-xl p-4" style={{ backgroundColor: '#FFF2F6' }}>
              <h3 className="font-bold text-sm mb-3 flex items-center gap-2"
                style={{ color: 'var(--color-primary-text)' }}>
                <MapPin size={16} style={{ color: 'var(--color-link)' }} /> Shipping Address
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--color-primary-text)' }}>
                {order.address || '-'}
                {order.city && `, ${order.city}`}
                {order.state && `, ${order.state}`}
                {order.postalCode && ` - ${order.postalCode}`}
              </p>
            </div>
          </div>
        </div>

        {/* ================= PRODUCTS ORDERED ================= */}
        <div
          className="rounded-2xl p-6 mb-6 anim-fadeInUp"
          style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}
        >
          <h2 className="text-xl font-bold mb-5 flex items-center gap-2"
            style={{ color: 'var(--color-primary-text)' }}>
            <Package size={22} style={{ color: 'var(--color-link)' }} />
            Products Ordered ({order.items?.length || 0})
          </h2>

          {order.items && order.items.length > 0 ? (
            <div className="space-y-3">
              {order.items.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 p-3 rounded-xl card-lift anim-fadeInUp"
                  style={{
                    backgroundColor: '#FFF2F6',
                    border: `1px solid var(--color-border)`,
                    animationDelay: `${i * 80}ms`,
                  }}
                >
                  <img
                    src={item.image || 'https://via.placeholder.com/80'}
                    alt={item.title}
                    className="w-16 h-16 md:w-20 md:h-20 object-cover rounded-lg shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${item.id}`}
                      className="font-semibold text-sm md:text-base line-clamp-2 hover:opacity-80"
                      style={{ color: 'var(--color-primary-text)' }}>
                      {item.title}
                    </Link>
                    <p className="text-xs mt-1" style={{ color: 'var(--color-secondary-text)' }}>
                      Qty: {item.quantity} × Rs. {item.price}
                    </p>
                  </div>
                  <p className="font-bold text-sm md:text-base shrink-0"
                    style={{ color: 'var(--color-link)' }}>
                    Rs. {item.price * item.quantity}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
              No items detail available.
            </p>
          )}
        </div>

        {/* ================= TOTALS ================= */}
        <div
          className="rounded-2xl p-6 mb-6 anim-fadeInUp"
          style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}
        >
          <h2 className="text-xl font-bold mb-5" style={{ color: 'var(--color-primary-text)' }}>
            Order Total
          </h2>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span style={{ color: 'var(--color-secondary-text)' }}>Subtotal</span>
              <span className="font-semibold" style={{ color: 'var(--color-primary-text)' }}>
                Rs. {order.subtotal || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'var(--color-secondary-text)' }}>Shipping</span>
              <span className="font-semibold"
                style={{ color: order.shipping === 0 ? 'var(--color-link)' : 'var(--color-primary-text)' }}>
                {order.shipping === 0 ? 'FREE' : `Rs. ${order.shipping || 0}`}
              </span>
            </div>
            <div className="border-t pt-3 mt-3 flex justify-between text-xl"
              style={{ borderColor: 'var(--color-border)' }}>
              <span className="font-bold" style={{ color: 'var(--color-primary-text)' }}>Total</span>
              <span className="font-bold" style={{ color: 'var(--color-link)' }}>
                Rs. {order.total || 0}
              </span>
            </div>
          </div>

          {order.paymentMethod === 'bank' && order.paymentStatus === 'pending-verification' && (
            <div className="mt-5 p-3 rounded-lg text-xs anim-pulseGlow"
              style={{ backgroundColor: '#FFF2F6', color: 'var(--color-primary-text)' }}>
              ⏳ Aapka bank transfer verify kiya ja raha hai. Jald hi confirmation milega.
            </div>
          )}
        </div>

        {/* ================= ACTION BUTTONS ================= */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8 anim-fadeInUp">
          <Link
            to="/"
            className="flex-1 rounded-full py-4 font-bold flex items-center justify-center gap-2 btn-2d shadow-lg"
            style={{ backgroundColor: 'var(--color-button-bg)', color: '#FFFFFF' }}
          >
            <Home size={20} /> Continue Shopping
          </Link>
          <button
            onClick={() => navigate('/shop')}
            className="flex-1 rounded-full py-4 font-bold flex items-center justify-center gap-2 btn-2d shadow-lg border-2"
            style={{
              borderColor: 'var(--color-button-bg)',
              color: 'var(--color-link)',
              backgroundColor: 'transparent',
            }}
          >
            <ShoppingBag size={20} /> Shop More
          </button>
        </div>

        {/* ================= ORDER SCREENSHOT HINT ================= */}
        <div className="text-center pb-6 anim-fadeInUp">
          <p className="text-xs" style={{ color: 'var(--color-secondary-text)' }}>
            .
          </p>
        </div>

      </div>
    </div>
  );
};

export default OrderSuccess;